/**
 * AI Plugin Detector
 * Identifies and classifies AI-related files from compressed archives
 * Supports: Claude, ChatGPT, Copilot, Skills, Agents, MCPs, and other AI frameworks
 */
/**
 * AI Framework Detection Patterns
 */
const AI_FRAMEWORK_PATTERNS = {
    // CLAUDE
    claude: {
        extensions: ['.claude', '.claude-plugin', '.claude-config'],
        fileNames: ['CLAUDE.md', 'claude.yaml', 'claude.json', '.claudeconfig'],
        directories: ['.claude/', '.claude-plugin/'],
        priority: 'CRITICAL'
    },
    // CHATGPT / OpenAI
    chatgpt: {
        extensions: ['.openai', '.gpt', '.openai-plugin'],
        fileNames: ['openai.yaml', 'openai.json', 'gpt-manifest.json', 'ai-plugin.json'],
        directories: ['.openai/', '.openai-plugin/'],
        priority: 'CRITICAL'
    },
    // COPILOT / Microsoft
    copilot: {
        extensions: ['.copilot', '.mscopilot', '.microsoft-agent'],
        fileNames: ['copilot.yaml', 'copilot.json', '.copilotrc', 'manifest.json'],
        directories: ['.copilot/', '.mscopilot/'],
        priority: 'CRITICAL'
    },
    // SKILLS
    skill: {
        extensions: ['.skill', '.skillset', '.skill-manifest'],
        fileNames: ['SKILL.md', 'skill.yaml', 'skill.json', 'skill-manifest.json'],
        directories: ['.skill/', 'skills/', 'skill-definitions/'],
        priority: 'HIGH'
    },
    // AGENTS
    agent: {
        extensions: ['.agent', '.agent-config', '.agent-manifest'],
        fileNames: ['AGENT.md', 'agent.yaml', 'agent.json', 'agents.yaml', 'Agents.md'],
        directories: ['.agent/', 'agents/', 'agent-definitions/'],
        priority: 'HIGH'
    },
    // MCPs (Model Context Protocol)
    mcp: {
        extensions: ['.mcp', '.mcp-server', '.mcp-config'],
        fileNames: ['mcp.yaml', 'mcp.json', 'mcp-manifest.json'],
        directories: ['.mcp/', 'mcp-servers/', 'mcp-definitions/'],
        priority: 'HIGH'
    },
    // OpenCode / Devin / Other agents
    opencode: {
        extensions: ['.opencode', '.devin', '.replit'],
        fileNames: ['opencode.yaml', 'opencode.json', '.opencoderc'],
        directories: ['.opencode/', '.devin/', '.replit/'],
        priority: 'HIGH'
    },
    // PLUGINS (Generic)
    plugin: {
        extensions: ['.plugin', '.plugin-manifest', '.plugin-config'],
        fileNames: ['plugin.yaml', 'plugin.json', 'plugin-manifest.json', 'manifest.json'],
        directories: ['plugins/', 'plugin-definitions/', '.plugins/'],
        priority: 'MEDIUM'
    },
    // PROMPTS / TEMPLATES
    prompt: {
        extensions: ['.prompt', '.template', '.jinja2', '.handlebars'],
        fileNames: ['prompt.md', 'prompts.yaml', 'templates.json'],
        directories: ['.prompt/', 'prompts/', 'templates/', '.github/prompts/'],
        priority: 'MEDIUM'
    },
    // HOOKS / INTEGRATIONS
    hook: {
        extensions: ['.hook', '.webhook', '.integration'],
        fileNames: ['hooks.yaml', 'hooks.json', 'integration.json'],
        directories: ['hooks/', '.hooks/', 'integrations/'],
        priority: 'MEDIUM'
    }
};
const CONTENT_PRIORITY_MAP = {
    CLAUDE: 'CRITICAL',
    CHATGPT: 'CRITICAL',
    COPILOT: 'CRITICAL',
    SKILL: 'HIGH',
    AGENT: 'HIGH',
    MCP: 'HIGH',
};
const AI_CONTENT_SCAN_LIMIT = 60;
function buildAISpecializedScopeMessage(sourceLabel) {
    return `SnapV is specialized for AI coding assets only. ${sourceLabel} must contain AI agent, MCP, skill, prompt, or model-integration artifacts before it can be scanned. Accepted examples: MCP servers/configs, SKILL.md, AGENT.md, OpenAI/Claude/Copilot plugin manifests, agent tool definitions, or archives/repositories that contain them.`;
}
function mergeDetectionEntry(entries, entry) {
    const existing = entries.get(entry.path);
    const severityOrder = {
        CRITICAL: 0,
        HIGH: 1,
        MEDIUM: 2,
        LOW: 3,
    };
    if (!existing || severityOrder[entry.severity] < severityOrder[existing.severity]) {
        entries.set(entry.path, entry);
    }
}
function normalizeDetection(result) {
    const aiPluginFiles = [...result.aiPluginFiles];
    const severityOrder = {
        CRITICAL: 0,
        HIGH: 1,
        MEDIUM: 2,
        LOW: 3,
    };
    aiPluginFiles.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    return {
        totalFiles: result.totalFiles,
        aiPluginFiles,
        detectedFrameworks: new Set(result.detectedFrameworks),
        vulnerabilityPriority: [...aiPluginFiles],
    };
}
/**
 * Detect AI plugin files from an archive
 */
export function detectAIPluginFiles(files) {
    const aiPluginFiles = [];
    const detectedFrameworks = new Set();
    files.forEach(file => {
        const detected = classifyAIFile(file.name);
        if (detected) {
            aiPluginFiles.push(detected);
            detectedFrameworks.add(detected.aiType);
        }
    });
    // Sort by severity and priority
    const vulnerabilityPriority = aiPluginFiles.sort((a, b) => {
        const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
        return severityOrder[a.severity] - severityOrder[b.severity];
    });
    return {
        totalFiles: files.length,
        aiPluginFiles,
        detectedFrameworks,
        vulnerabilityPriority
    };
}
export function validateAISpecializedFileSet(files, sourceLabel = 'This source') {
    const fileDetection = detectAIPluginFiles(files);
    const mergedEntries = new Map();
    const detectedFrameworks = new Set();
    const matchedFiles = new Set();
    fileDetection.aiPluginFiles.forEach((entry) => {
        mergeDetectionEntry(mergedEntries, entry);
        detectedFrameworks.add(entry.aiType.toUpperCase());
        matchedFiles.add(entry.path);
    });
    files.slice(0, AI_CONTENT_SCAN_LIMIT).forEach((file) => {
        if (!file.content?.trim())
            return;
        const contentDetection = detectAIPluginInPastedCode(file.content, file.name);
        if (!contentDetection.detectedFrameworks.length)
            return;
        const primaryFramework = contentDetection.framework === 'UNKNOWN'
            ? contentDetection.detectedFrameworks[0]
            : contentDetection.framework;
        if (!primaryFramework)
            return;
        mergeDetectionEntry(mergedEntries, {
            path: file.name,
            fileName: file.name.split('/').pop() || file.name,
            extension: file.name.includes('.') ? file.name.split('.').pop() || '' : '',
            aiType: primaryFramework.toLowerCase(),
            fileType: getFileType(file.name),
            severity: CONTENT_PRIORITY_MAP[primaryFramework] || 'MEDIUM',
            reason: `Detected ${primaryFramework} patterns in file content`,
        });
        contentDetection.detectedFrameworks.forEach((framework) => detectedFrameworks.add(framework.toUpperCase()));
        matchedFiles.add(file.name);
    });
    const mergedDetection = normalizeDetection({
        totalFiles: files.length,
        aiPluginFiles: Array.from(mergedEntries.values()),
        detectedFrameworks,
        vulnerabilityPriority: Array.from(mergedEntries.values()),
    });
    if (mergedDetection.aiPluginFiles.length === 0) {
        return {
            isValid: false,
            message: buildAISpecializedScopeMessage(sourceLabel),
            detection: mergedDetection,
            detectedFrameworks: [],
            matchedFiles: [],
        };
    }
    return {
        isValid: true,
        message: null,
        detection: mergedDetection,
        detectedFrameworks: Array.from(detectedFrameworks),
        matchedFiles: Array.from(matchedFiles),
    };
}
export function validateAISpecializedSingleFile(fileName, content, sourceLabel = 'This file') {
    return validateAISpecializedFileSet([{ name: fileName, content }], sourceLabel);
}
/**
 * Classify a single file as AI plugin type
 */
export function classifyAIFile(filePath) {
    const fileName = filePath.split('/').pop() || '';
    const lowerPath = filePath.toLowerCase();
    const lowerName = fileName.toLowerCase();
    for (const [framework, patterns] of Object.entries(AI_FRAMEWORK_PATTERNS)) {
        // Check extensions
        if (patterns.extensions.some(ext => lowerPath.includes(ext))) {
            return {
                path: filePath,
                fileName,
                extension: filePath.split('.').pop() || '',
                aiType: framework,
                fileType: getFileType(filePath),
                severity: patterns.priority,
                reason: `Detected ${framework.toUpperCase()} plugin file`
            };
        }
        // Check file names
        if (patterns.fileNames.some(name => lowerName === name)) {
            return {
                path: filePath,
                fileName,
                extension: filePath.split('.').pop() || '',
                aiType: framework,
                fileType: getFileType(filePath),
                severity: patterns.priority,
                reason: `Detected ${framework.toUpperCase()} ${getFileType(filePath)}`
            };
        }
        // Check directories
        if (patterns.directories.some(dir => lowerPath.includes(dir))) {
            return {
                path: filePath,
                fileName,
                extension: filePath.split('.').pop() || '',
                aiType: framework,
                fileType: getFileType(filePath),
                severity: patterns.priority,
                reason: `Found in ${framework.toUpperCase()} directory`
            };
        }
    }
    return null;
}
/**
 * Determine file type based on content and extension
 */
function getFileType(filePath) {
    const lower = filePath.toLowerCase();
    if (lower.includes('manifest'))
        return 'manifest';
    if (lower.includes('schema'))
        return 'schema';
    if (lower.match(/\.(yaml|yml|json)$/))
        return 'config';
    if (lower.match(/\.(md|txt)$/))
        return 'documentation';
    if (lower.match(/\.(py|js|ts|tsx)$/))
        return 'code';
    if (lower.match(/\.(mcp|skill|agent)$/))
        return 'definition';
    return 'unknown';
}
/**
 * Generate AI Plugin Vulnerability Report
 */
export function generateAIPluginReport(result) {
    let report = '';
    report += '='.repeat(70) + '\n';
    report += 'AI PLUGIN & AGENT SECURITY SCAN REPORT\n';
    report += '='.repeat(70) + '\n\n';
    report += `📊 DETECTION SUMMARY\n`;
    report += `Total Files Scanned: ${result.totalFiles}\n`;
    report += `AI Plugin Files Found: ${result.aiPluginFiles.length}\n`;
    report += `Detected Frameworks: ${Array.from(result.detectedFrameworks).join(', ')}\n\n`;
    if (result.detectedFrameworks.size === 0) {
        report += '⏭️  No AI plugins detected in this archive.\n';
        return report;
    }
    report += '🔍 DETECTED FRAMEWORKS\n';
    result.detectedFrameworks.forEach(framework => {
        const count = result.aiPluginFiles.filter(f => f.aiType === framework).length;
        report += `  • ${framework.toUpperCase()}: ${count} file(s)\n`;
    });
    report += '\n⚠️  CRITICAL & HIGH-PRIORITY PLUGIN FILES\n';
    result.vulnerabilityPriority.forEach((file, idx) => {
        report += `\n${idx + 1}. ${file.fileName}\n`;
        report += `   Path: ${file.path}\n`;
        report += `   Type: ${file.aiType.toUpperCase()} - ${file.fileType}\n`;
        report += `   Priority: ${file.severity}\n`;
        report += `   Reason: ${file.reason}\n`;
    });
    report += '\n' + '='.repeat(70) + '\n';
    return report;
}
/**
 * Filter files for focused AI plugin scanning
 */
export function filterAIPluginFiles(allFiles) {
    return allFiles.filter(file => {
        const detected = classifyAIFile(file.name);
        return detected !== null;
    });
}
/**
 * Get priority files for immediate scanning
 * (CRITICAL and HIGH priority plugins)
 */
export function getPriorityAIPluginFiles(allFiles) {
    const result = detectAIPluginFiles(allFiles);
    const priorityFiles = result.vulnerabilityPriority
        .filter(f => f.severity === 'CRITICAL' || f.severity === 'HIGH')
        .map(pf => {
        const file = allFiles.find(f => f.name === pf.path);
        return {
            name: pf.path,
            content: file?.content || '',
            priority: pf.severity
        };
    });
    return priorityFiles;
}
/**
 * PASTED CODE DETECTION
 * Analyzes the content of pasted code to detect AI plugin configurations
 */
export function detectAIPluginInPastedCode(content, fileName = 'pasted_code') {
    const detectedFrameworks = [];
    let primaryFramework = 'UNKNOWN';
    let confidence = 'LOW';
    // CLAUDE Detection Patterns
    const claudePatterns = [
        /claude\.md/i,
        /\bclaude\b/i,
        /claude[_-]?api[_-]?key/i,
        /anthropic[_-]?api[_-]?key/i,
        /\banthropic\b/i,
        /claude[_-]?model/i,
        /model\s*:\s*["']?claude/i,
        /messages\.claude/i,
        /tool[_-]?use/i,
        /claude[_-]?tool/i,
        /tool_use_id/i,
        /content["\']:\s*\[[\s\S]*?type["\']:\s*["\']tool_use/i
    ];
    // CHATGPT/OpenAI Detection Patterns
    const chatgptPatterns = [
        /chatgpt/i,
        /\bopenai\b/i,
        /openai[_-]?api[_-]?key/i,
        /gpt[_-]?[34]/i,
        /gpt[_-]?model/i,
        /model\s*:\s*["']?gpt/i,
        /openai[_-]?organization/i,
        /completions[_-]?api/i,
        /chat[_-]?completions/i,
        /plugin[_-]?manifest/i,
        /ai[_-]?plugin/i
    ];
    // COPILOT Detection Patterns
    const copilotPatterns = [
        /copilot/i,
        /copilot[_-]?api[_-]?key/i,
        /github[_-]?copilot/i,
        /microsoft[_-]?copilot/i,
        /copilot[_-]?extension/i,
        /skill[_-]?manifest["\']:\s*\{/i
    ];
    // SKILL Detection Patterns
    const skillPatterns = [
        /skill\.md/i,
        /skill[_-]?(?:name|id|definition)/i,
        /skill[_-]?(?:handler|function)/i,
        /skills["\']:\s*\[/i,
        /skill[_-]?manifest/i,
        /skill["\']:\s*\{[\s\S]*?(?:name|description|handler)/i
    ];
    // AGENT Detection Patterns
    const agentPatterns = [
        /agent\.md/i,
        /agent[_-]?(?:name|id|definition)/i,
        /agent[_-]?(?:handler|action)/i,
        /agents["\']:\s*\[/i,
        /agent[_-]?manifest/i,
        /agent["\']:\s*\{[\s\S]*?(?:name|description|tools)/i,
        /autonomous[_-]?agent/i
    ];
    // MCP Detection Patterns
    const mcpPatterns = [
        /\bmcp\b/i,
        /mcp[_-]?(?:server|client)/i,
        /model[_-]?context[_-]?protocol/i,
        /mcp["\']:\s*\{/i,
        /mcp[_-]?manifest/i,
        /mcp[_-]?handler/i,
        /resources["\']:\s*\[[\s\S]*?name["\']:/i
    ];
    // Count pattern matches for confidence
    const claudeMatches = claudePatterns.filter(p => p.test(content)).length;
    const chatgptMatches = chatgptPatterns.filter(p => p.test(content)).length;
    const copilotMatches = copilotPatterns.filter(p => p.test(content)).length;
    const skillMatches = skillPatterns.filter(p => p.test(content)).length;
    const agentMatches = agentPatterns.filter(p => p.test(content)).length;
    const mcpMatches = mcpPatterns.filter(p => p.test(content)).length;
    // Debug logging
    console.log('🔍 Pattern Matching Details:', {
        claude: { matches: claudeMatches, patterns: claudePatterns.length, matched: claudePatterns.filter(p => p.test(content)).map((p, i) => `pattern${i}`) },
        chatgpt: { matches: chatgptMatches, patterns: chatgptPatterns.length },
        copilot: { matches: copilotMatches, patterns: copilotPatterns.length },
        skill: { matches: skillMatches, patterns: skillPatterns.length },
        agent: { matches: agentMatches, patterns: agentPatterns.length },
        mcp: { matches: mcpMatches, patterns: mcpPatterns.length }
    });
    // Determine detected frameworks
    if (claudeMatches > 0)
        detectedFrameworks.push('CLAUDE');
    if (chatgptMatches > 0)
        detectedFrameworks.push('CHATGPT');
    if (copilotMatches > 0)
        detectedFrameworks.push('COPILOT');
    if (skillMatches > 0)
        detectedFrameworks.push('SKILL');
    if (agentMatches > 0)
        detectedFrameworks.push('AGENT');
    if (mcpMatches > 0)
        detectedFrameworks.push('MCP');
    // Find primary framework (most matches)
    const scores = [
        { framework: 'CLAUDE', score: claudeMatches },
        { framework: 'CHATGPT', score: chatgptMatches },
        { framework: 'COPILOT', score: copilotMatches },
        { framework: 'SKILL', score: skillMatches },
        { framework: 'AGENT', score: agentMatches },
        { framework: 'MCP', score: mcpMatches }
    ].sort((a, b) => b.score - a.score);
    if (scores[0].score > 0) {
        primaryFramework = scores[0].framework;
        confidence = scores[0].score >= 3 ? 'HIGH' : scores[0].score >= 1 ? 'MEDIUM' : 'LOW';
    }
    // Generate details
    const details = `
Detected Frameworks: ${detectedFrameworks.length > 0 ? detectedFrameworks.join(', ') : 'NONE'}
Primary Framework: ${primaryFramework}
Confidence: ${confidence}

Pattern Matches:
  • Claude: ${claudeMatches} patterns
  • ChatGPT/OpenAI: ${chatgptMatches} patterns
  • Copilot: ${copilotMatches} patterns
  • Skills: ${skillMatches} patterns
  • Agents: ${agentMatches} patterns
  • MCPs: ${mcpMatches} patterns
`.trim();
    return {
        detectedFrameworks,
        framework: primaryFramework,
        confidence,
        details
    };
}
